import { useEffect, useRef } from 'react'

import { RumEvent, datadogRum } from '@datadog/browser-rum'
import { matchRoutes } from 'react-router-dom'

import { DEFAULT_ROUTES, PRIVATE_ROUTES, PUBLIC_ROUTES } from 'routes/routes'

// Manual RUM view tracking (sc-260370). With automatic tracking the SDK named
// views after the raw URL, so casing split one page into two
// (/Accounts/home vs /accounts/home), every legacy CakePHP page reported as
// joor-react, and React-to-legacy navigations left a ~40ms route_change view
// behind before LegacyPage reloaded the browser.

export const REACT_SERVICE = 'joor-react'
// Same name as the CakePHP backend's APM service, so a slow legacy view points
// straight at the codebase that owns it.
export const LEGACY_SERVICE = 'web-php'

const REACT_ROUTES = [
  ...PRIVATE_ROUTES,
  ...PUBLIC_ROUTES,
  ...DEFAULT_ROUTES,
].map(({ path }) => ({ path }))

// Same convention as Datadog's own URL grouping (any segment with a digit
// becomes `?`), so names stay comparable with the history we already have.
const normalizeSegment = (segment: string) =>
  segment.startsWith(':') || /\d/.test(segment) ? '?' : segment.toLowerCase()

const toViewName = (path: string) =>
  `/${path.split('/').filter(Boolean).map(normalizeSegment).join('/')}`

export const getRumViewName = (pathname: string) => {
  const pattern = matchRoutes(REACT_ROUTES, pathname)?.[0]?.route.path
  // Catch-alls like /ra/* say nothing about the page, so keep the URL instead.
  return toViewName(pattern && !pattern.includes('*') ? pattern : pathname)
}

// Legacy `#!` pages route in-page through the hash without reloading, and
// automatic tracking started a view per `#!` route. Keep doing that (query
// stripped, like the SDK's areDifferentLocation); other hashes are anchors.
const getHashRoute = (hash: string) =>
  hash.startsWith('#!') ? hash.split('?')[0] : ''

const toLocationKey = (pathname: string, hash = '') =>
  pathname + getHashRoute(hash)

let isEnabled = false
let isPageReloading = false
let isLegacyPageMounted = false
let currentLocationKey: string | undefined
const legacyViewIds = new Set<string>()

const markCurrentViewAsLegacy = () => {
  const viewId = datadogRum.getInternalContext()?.view?.id
  if (viewId) {
    legacyViewIds.add(viewId)
  }
}

export const startInitialRumView = () => {
  isEnabled = true
  const { pathname, hash } = window.location
  currentLocationKey = toLocationKey(pathname, hash)
  // The first manual view still starts at navigation start, but RUM collects
  // nothing until it is called, so this must run right after init.
  datadogRum.startView({ name: getRumViewName(pathname) })
}

const startRumView = (pathname: string, hash: string) => {
  const locationKey = toLocationKey(pathname, hash)
  if (!isEnabled || locationKey === currentLocationKey) {
    return
  }
  if (isPageReloading) {
    // LegacyPage is reloading the browser: the reload starts its own
    // initial_load view, so starting one here would only add a transient
    // view. Only this navigation is skipped: if the reload never happens
    // (e.g. the user stays on a beforeunload prompt), later ones still count.
    isPageReloading = false
    return
  }
  currentLocationKey = locationKey
  datadogRum.startView({ name: getRumViewName(pathname) })
  if (isLegacyPageMounted) {
    // PHP changed the URL without a reload: still the same legacy page.
    markCurrentViewAsLegacy()
  }
}

// WHY a ref to the owner instead of passing `service` to startView: ownership
// is only known once RouterPage or LegacyPage mounts (feature-flagged routes
// such as /accounts/home render either one), seconds after the view started.
export const setLegacyPageMounted = (mounted: boolean) => {
  isLegacyPageMounted = mounted
  if (mounted) {
    markCurrentViewAsLegacy()
  }
}

export const markPageReloading = () => {
  isPageReloading = true
}

// beforeSend hook: `service` is one of the fields the SDK lets beforeSend
// rewrite, and view events are re-sent on every update, so the final view
// event carries the right owner even when it was learned late.
export const applyRumViewService = (event: RumEvent) => {
  if (event.view?.id && legacyViewIds.has(event.view.id)) {
    // eslint-disable-next-line no-param-reassign
    event.service = LEGACY_SERVICE
  }
}

export const useRumViewTracking = (pathname: string, hash = '') => {
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      // Boot-time URL rewrites (auth, redirects) are not real navigations:
      // rename the initial view instead of starting a new one.
      const locationKey = toLocationKey(pathname, hash)
      if (isEnabled && locationKey !== currentLocationKey) {
        currentLocationKey = locationKey
        datadogRum.setViewName(getRumViewName(pathname))
      }
      return undefined
    }

    // Deferred so that a <Navigate replace> redirect in the same commit
    // cancels this view instead of leaving a few-ms one behind, and so that
    // LegacyPage has already flagged a reload by the time it runs.
    const timer = setTimeout(() => startRumView(pathname, hash), 0)
    return () => clearTimeout(timer)
  }, [pathname, hash])
}
